import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Button, Progress } from '@/components/ui'
import { PauseIcon, PlayIcon, StopIcon, WarningIcon } from '@/components/icons'
import type { BreathingExercise } from '../exercises'
import { SAFETY_CUE } from '../exercises'
import { completedCycles, formatMinutesSeconds, phaseAt } from '../timing'
import type { SoundCues } from '../soundCues'
import type { HapticCues } from '../haptics'
import { BreathingCircle } from './BreathingCircle'

export type SessionViewProps = {
  exercise: BreathingExercise
  durationMinutes: number
  reducedMotion: boolean
  sounds: SoundCues
  haptics: HapticCues
  /** Fired once when the full duration elapsed. */
  onComplete: (elapsedSeconds: number) => void
  /** Fired once when the session is stopped or the page is hidden mid-session. */
  onInterrupt: (elapsedSeconds: number) => void
  /** Fired when the countdown is cancelled before the session started. */
  onCancelCountdown: () => void
}

type SessionStage = 'countdown' | 'active'

const COUNTDOWN_FROM = 3

/**
 * 3-2-1 countdown followed by the active breathing session.
 * The stop action is always visible. All timers are cleaned up on unmount.
 */
export function SessionView({
  exercise,
  durationMinutes,
  reducedMotion,
  sounds,
  haptics,
  onComplete,
  onInterrupt,
  onCancelCountdown,
}: SessionViewProps) {
  const durationSeconds = durationMinutes * 60

  const [stage, setStage] = useState<SessionStage>('countdown')
  const [count, setCount] = useState(COUNTDOWN_FROM)
  const [paused, setPaused] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [announcement, setAnnouncement] = useState(
    `De oefening begint over ${COUNTDOWN_FROM}`,
  )

  const clockRef = useRef({ accumulated: 0, runStart: 0 })
  const finishedRef = useRef(false)
  const lastPhaseKeyRef = useRef('')
  const stageRef = useRef<SessionStage>(stage)
  const pausedRef = useRef(paused)
  stageRef.current = stage
  pausedRef.current = paused

  const onCompleteRef = useRef(onComplete)
  const onInterruptRef = useRef(onInterrupt)
  const onCancelRef = useRef(onCancelCountdown)
  onCompleteRef.current = onComplete
  onInterruptRef.current = onInterrupt
  onCancelRef.current = onCancelCountdown

  const currentElapsedSeconds = useCallback(
    () =>
      Math.floor(
        (clockRef.current.accumulated +
          (stageRef.current === 'active' && !pausedRef.current
            ? performance.now() - clockRef.current.runStart
            : 0)) /
          1000,
      ),
    [],
  )

  // --- Countdown: 3, 2, 1 → active ---------------------------------------
  useEffect(() => {
    if (stage !== 'countdown') return
    if (count <= 0) {
      clockRef.current = { accumulated: 0, runStart: performance.now() }
      setStage('active')
      setAnnouncement('Start. Adem mee met de cirkel.')
      return
    }
    setAnnouncement(
      count === COUNTDOWN_FROM ? `De oefening begint over ${count}` : String(count),
    )
    const id = window.setTimeout(() => setCount((value) => value - 1), 1000)
    return () => window.clearTimeout(id)
  }, [stage, count])

  // --- Active session clock (100ms ticks, pausable) -----------------------
  useEffect(() => {
    if (stage !== 'active' || paused || finishedRef.current) return
    const id = window.setInterval(() => {
      const elapsed =
        clockRef.current.accumulated + (performance.now() - clockRef.current.runStart)
      setElapsedMs(elapsed)
      if (elapsed >= durationSeconds * 1000 && !finishedRef.current) {
        finishedRef.current = true
        window.clearInterval(id)
        sounds.cueDone()
        haptics.cueDone()
        onCompleteRef.current(durationSeconds)
      }
    }, 100)
    return () => window.clearInterval(id)
  }, [stage, paused, durationSeconds, sounds, haptics])

  // --- Phase cues: text announcement + optional sound/haptic --------------
  const position = phaseAt(exercise, elapsedMs / 1000)
  useEffect(() => {
    if (stage !== 'active' || paused) return
    const key = `${position.cycleIndex}:${position.phaseIndex}`
    if (lastPhaseKeyRef.current === key) return
    lastPhaseKeyRef.current = key
    setAnnouncement(position.phase.announce)
    switch (position.phase.kind) {
      case 'in':
        sounds.cueIn()
        haptics.cueIn()
        break
      case 'hold':
        sounds.cueHold()
        haptics.cueHold()
        break
      case 'out':
        sounds.cueOut()
        haptics.cueOut()
        break
      case 'rest':
        break
    }
  }, [stage, paused, position, sounds, haptics])

  const finishInterrupted = useCallback(() => {
    if (finishedRef.current) return
    finishedRef.current = true
    onInterruptRef.current(currentElapsedSeconds())
  }, [currentElapsedSeconds])

  // --- Page hidden mid-session → interrupted, never falsely complete ------
  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden || finishedRef.current) return
      if (stageRef.current === 'countdown') {
        finishedRef.current = true
        onCancelRef.current()
      } else if (stageRef.current === 'active' && !pausedRef.current) {
        finishInterrupted()
      }
      // An intentionally paused session stays paused when the page hides.
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [finishInterrupted])

  const handleStop = useCallback(() => {
    if (finishedRef.current) return
    if (stageRef.current === 'countdown') {
      finishedRef.current = true
      onCancelRef.current()
    } else {
      finishInterrupted()
    }
  }, [finishInterrupted])

  // Keyboard: Escape stops immediately.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleStop()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleStop])

  const togglePause = useCallback(() => {
    if (pausedRef.current) {
      clockRef.current.runStart = performance.now()
      setPaused(false)
      setAnnouncement('Hervat. Adem mee met de cirkel.')
    } else {
      clockRef.current.accumulated += performance.now() - clockRef.current.runStart
      setPaused(true)
      setAnnouncement('Gepauzeerd')
    }
  }, [])

  const remainingSeconds = Math.max(0, durationSeconds - elapsedMs / 1000)
  const sessionProgress = Math.min(1, elapsedMs / 1000 / durationSeconds)
  const breathCount = completedCycles(exercise, elapsedMs / 1000)

  return (
    <div className="breathing-session">
      <p className="breathing-safety" role="note">
        <WarningIcon size={18} />
        {SAFETY_CUE}
      </p>

      <div className="visually-hidden" aria-live="polite">
        {announcement}
      </div>

      {stage === 'countdown' ? (
        <div className="breathing-countdown">
          <p className="breathing-countdown__hint">{exercise.name} begint over</p>
          {reducedMotion ? (
            <p className="breathing-countdown__number">{count}</p>
          ) : (
            <motion.p
              key={count}
              className="breathing-countdown__number"
              initial={{ scale: 1.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              {count}
            </motion.p>
          )}
        </div>
      ) : (
        <>
          <BreathingCircle
            exercise={exercise}
            position={position}
            paused={paused}
            reducedMotion={reducedMotion}
          />
          <div className="breathing-session__meta">
            <p className="breathing-session__remaining">
              Nog {formatMinutesSeconds(remainingSeconds)}
            </p>
            <p className="breathing-session__breaths">Ronde {breathCount + 1}</p>
          </div>
          <Progress value={sessionProgress} aria-label="Voortgang van de oefening" />
        </>
      )}

      <div className="breathing-session__actions">
        {stage === 'active' && (
          <Button variant="secondary" size="lg" onClick={togglePause}>
            <span className="breathing-action">
              {paused ? (
                <PlayIcon size={20} />
              ) : (
                <PauseIcon size={20} />
              )}
              {paused ? 'Hervat' : 'Pauzeer'}
            </span>
          </Button>
        )}
        <Button
          variant="danger"
          size="lg"
          fullWidth={stage === 'countdown'}
          onClick={handleStop}
        >
          <span className="breathing-action">
            <StopIcon size={20} />
            Stop
          </span>
        </Button>
      </div>
    </div>
  )
}
